import React, { useState, useEffect, useRef } from "react";
import {
  CheckCircle,
  RotateCcw,
  Trophy,
  AlertCircle,
  Sparkles,
  Target,
} from "lucide-react";
import Markdown from "react-markdown";
import { tC } from "../../utils/TranslateWithContentLanguage";

interface VocabularyWord {
  id: number;
  word: string;
  usage: string;
  explanation: string;
  imageUrl?: string;
}

interface DragDropQuizProps {
  vocabulary: VocabularyWord[];
  contentLanguage?: string;
}

interface QuizItem {
  id: string;
  word: string;
  definition: string;
  usage?: string;
  originalIndex: number;
}

interface DropZone {
  id: string;
  definition: string;
  usage?: string;
  droppedItem: QuizItem | null;
  correctAnswer: string;
}

// Sample vocabulary data for demo
const sampleVocabulary: VocabularyWord[] = [
  {
    id: 1,
    word: "Resilient",
    usage: "The resilient student bounced back from failure.",
    explanation:
      "Able to recover quickly from difficulties; tough and flexible.",
  },
  {
    id: 2,
    word: "Innovative",
    usage: "Her innovative approach solved the complex problem.",
    explanation: "Featuring new methods; advanced and original in thinking.",
  },
  {
    id: 3,
    word: "Perseverance",
    usage: "Through perseverance, he mastered the difficult skill.",
    explanation: "Persistence in doing something despite difficulty or delay.",
  },
  {
    id: 4,
    word: "Empathy",
    usage: "She showed empathy by understanding his feelings.",
    explanation: "The ability to understand and share others' feelings.",
  },
  {
    id: 5,
    word: "Articulate",
    usage: "The articulate speaker captivated the audience.",
    explanation: "Having or showing the ability to speak fluently and clearly.",
  },
  {
    id: 6,
    word: "Collaborate",
    usage: "Students collaborate on the group project.",
    explanation: "To work jointly on an activity or project with others.",
  },
];

const DragDropVocab: React.FC<DragDropQuizProps> = ({
  vocabulary = sampleVocabulary,
  contentLanguage = "tr",
}) => {
  const [quizItems, setQuizItems] = useState<QuizItem[]>([]);
  const [dropZones, setDropZones] = useState<DropZone[]>([]);
  const [draggedItem, setDraggedItem] = useState<QuizItem | null>(null);
  const [dragOverZone, setDragOverZone] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [celebration, setCelebration] = useState(false);
  const [touchStartPos, setTouchStartPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);

  // Helper function to shuffle array
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const initializeQuiz = () => {
    const limitedVocabulary = vocabulary.slice(0, 6);

    const items: QuizItem[] = limitedVocabulary.map((vocab, index) => ({
      id: `word-${index}`,
      word: vocab.word || "",
      definition: vocab.explanation || "",
      usage: vocab.usage,
      originalIndex: index,
    }));

    const zones: DropZone[] = limitedVocabulary.map((vocab, index) => ({
      id: `zone-${index}`,
      definition: vocab.explanation || "",
      usage: vocab.usage,
      droppedItem: null,
      correctAnswer: vocab.word || "",
    }));

    setQuizItems(shuffleArray(items));
    setDropZones(zones);
    setShowResults(false);
    setScore(0);
    setCelebration(false);
    setDraggedItem(null);
    setDragOverZone(null);
  };

  useEffect(() => {
    if (vocabulary.length > 0) {
      initializeQuiz();
    }
  }, [vocabulary]);

  // Touch and mouse event handlers
  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    item: QuizItem,
  ) => {
    setDraggedItem(item);
    setIsDragging(true);

    if ("dataTransfer" in e) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", item.id);
    }
  };

  const handleTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    item: QuizItem,
  ) => {
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setDraggedItem(item);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!touchStartPos || !draggedItem) return;

    e.preventDefault();
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.y);

    // Start dragging if moved enough
    if ((deltaX > 10 || deltaY > 10) && !isDragging) {
      setIsDragging(true);
    }

    // Find element under touch
    const elementBelow = document.elementFromPoint(
      touch.clientX,
      touch.clientY,
    );
    const dropZone = elementBelow?.closest("[data-drop-zone]");

    if (dropZone) {
      const zoneId = dropZone.getAttribute("data-drop-zone");
      setDragOverZone(zoneId);
    } else {
      setDragOverZone(null);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!draggedItem || !isDragging) {
      setTouchStartPos(null);
      setDraggedItem(null);
      setIsDragging(false);
      return;
    }

    const touch = e.changedTouches[0];
    const elementBelow = document.elementFromPoint(
      touch.clientX,
      touch.clientY,
    );
    const dropZone = elementBelow?.closest("[data-drop-zone]");

    if (dropZone) {
      const zoneId = dropZone.getAttribute("data-drop-zone");
      if (zoneId) {
        handleDrop(zoneId);
      }
    }

    setTouchStartPos(null);
    setDraggedItem(null);
    setIsDragging(false);
    setDragOverZone(null);
  };

  const handleDrop = (zoneId: string) => {
    if (!draggedItem) return;

    setDropZones((prev) =>
      prev.map((zone) => {
        if (zone.id === zoneId) {
          // If zone has an item, return it to available items
          if (zone.droppedItem) {
            setQuizItems((current) => [...current, zone.droppedItem!]);
          }
          return { ...zone, droppedItem: draggedItem };
        }
        return zone;
      }),
    );

    // Remove item from available items
    setQuizItems((prev) => prev.filter((item) => item.id !== draggedItem.id));
    setDraggedItem(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (
    e: React.DragEvent<HTMLDivElement>,
    zoneId: string,
  ) => {
    e.preventDefault();
    setDragOverZone(zoneId);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOverZone(null);
  };

  const handleDropEvent = (
    e: React.DragEvent<HTMLDivElement>,
    zoneId: string,
  ) => {
    e.preventDefault();
    handleDrop(zoneId);
    setDragOverZone(null);
  };

  const returnToWords = (item: QuizItem) => {
    setQuizItems((prev) => [...prev, item]);
    setDropZones((prev) =>
      prev.map((zone) =>
        zone.droppedItem?.id === item.id
          ? { ...zone, droppedItem: null }
          : zone,
      ),
    );
  };

  const checkAnswers = () => {
    let correctCount = 0;
    dropZones.forEach((zone) => {
      if (zone.droppedItem && zone.droppedItem.word === zone.correctAnswer) {
        correctCount++;
      }
    });

    setScore(correctCount);
    setShowResults(true);

    if (correctCount === dropZones.length) {
      setCelebration(true);
      setTimeout(() => setCelebration(false), 3000);
    }
  };

  const resetQuiz = () => {
    initializeQuiz();
  };

  const allItemsPlaced = dropZones.every((zone) => zone.droppedItem !== null);
  const progress =
    (dropZones.filter((zone) => zone.droppedItem !== null).length /
      dropZones.length) *
    100;

  if (vocabulary.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-100 via-blue-50 to-cyan-100 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 shadow-2xl text-center">
          <AlertCircle className="w-20 h-20 text-gray-400 mx-auto mb-6" />
          <p className="text-2xl text-gray-600">
            {tC(contentLanguage, "vocabularies.noVocabularyAvailable")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-blue-50 to-cyan-100 relative overflow-hidden select-none">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200/70 rounded-full mix-blend-multiply filter blur-xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-200/70 rounded-full mix-blend-multiply filter blur-xl animate-float-delayed"></div>
        <div className="absolute top-40 left-1/2 w-80 h-80 bg-blue-200/70 rounded-full mix-blend-multiply filter blur-xl animate-float-slow"></div>
      </div>

      {/* Celebration animation */}
      {celebration && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <div className="text-8xl animate-bounce">🎉</div>
          <div className="absolute animate-ping">
            <Sparkles className="w-24 h-24 text-yellow-400" />
          </div>
          <div className="absolute animate-pulse text-6xl">✨</div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-8 pt-8">
        {/* Header */}
        <div className="text-center">
          {/* Control Buttons */}
          <div className="flex justify-center gap-6 mb-8">
            <button
              onClick={checkAnswers}
              disabled={!allItemsPlaced}
              className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg ${
                allItemsPlaced
                  ? "bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:shadow-green-300 hover:shadow-xl active:scale-95"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              <CheckCircle size={24} className="inline mr-3" />
              {tC(contentLanguage, "common.checkAnswers")}
            </button>

            <button
              onClick={resetQuiz}
              className="px-8 py-4 rounded-2xl font-semibold text-lg bg-gradient-to-r from-blue-500 to-blue-500 text-white transition-all duration-300 transform hover:scale-105 hover:shadow-blue-300 hover:shadow-xl shadow-lg active:scale-95"
            >
              <RotateCcw size={24} className="inline mr-3" />
              {tC(contentLanguage, "common.reset")}
            </button>
          </div>

          {/* Results */}
          {showResults && (
            <div className="max-w-lg mx-auto bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-2xl border border-white/20 animate-slide-up mb-12">
              <div className="flex items-center justify-center gap-4 text-2xl font-bold mb-4">
                <Trophy className="w-10 h-10 text-yellow-500 animate-bounce" />
                <span className="bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                  {tC(contentLanguage, "common.score")}: {score}/
                  {dropZones.length}
                </span>
              </div>
              {score === dropZones.length ? (
                <div className="text-center">
                  <p className="text-xl text-green-600 font-semibold mb-2">
                    {tC(contentLanguage, "vocabularies.perfectScore")}
                  </p>
                  <p className="text-gray-600">
                    {tC(contentLanguage, "vocabularies.championMessage")}
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-lg text-blue-600 font-medium mb-2">
                    {tC(contentLanguage, "vocabularies.keepPracticing")}
                  </p>
                  <p className="text-gray-600">
                    {tC(contentLanguage, "vocabularies.scoreMessage", {
                      score,
                      total: dropZones.length,
                    })}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Words to Drag - Horizontal Row */}
          <div>
            <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
              {tC(contentLanguage, "vocabularies.words")}
            </h2>
            <div className="min-h-[200px] p-8 rounded-3xl border-4 border-dashed border-gray-300 bg-white/40 backdrop-blur-sm transition-all duration-300">
              <div className="flex flex-wrap justify-center items-center gap-6 min-h-[120px]">
                {quizItems.map((item, index) => (
                  <div
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    onTouchStart={(e) => handleTouchStart(e, item)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    className={`px-8 py-6 rounded-2xl cursor-grab active:cursor-grabbing transition-all duration-300 min-w-[200px] text-center shadow-lg select-none ${
                      draggedItem?.id === item.id && isDragging
                        ? "bg-gradient-to-r from-blue-400 to-blue-400 text-white scale-110 rotate-3 z-50 shadow-2xl"
                        : "bg-white/90 backdrop-blur-sm border-2 border-white/50 hover:shadow-xl hover:scale-105 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-50 active:scale-95"
                    }`}
                    style={{
                      touchAction: "none",
                      userSelect: "none",
                    }}
                  >
                    <div className="text-xl font-bold text-gray-800 pointer-events-none">
                      {item.word}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Drop Zones (Definitions) - 3x2 Grid */}
          <div>
            <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
              {tC(contentLanguage, "vocabularies.definitions")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {dropZones.map((zone, index) => (
                <div
                  key={zone.id}
                  data-drop-zone={zone.id}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, zone.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDropEvent(e, zone.id)}
                  className={`p-6 rounded-3xl border-4 border-dashed min-h-[200px] transition-all duration-300 shadow-lg ${
                    dragOverZone === zone.id
                      ? "border-green-400 bg-green-100/50 scale-105 shadow-green-200 shadow-xl"
                      : zone.droppedItem
                        ? showResults
                          ? zone.droppedItem.word === zone.correctAnswer
                            ? "border-green-500 bg-green-100/70 shadow-green-200 shadow-xl"
                            : "border-red-500 bg-red-100/70 shadow-red-200 shadow-xl"
                          : "border-blue-400 bg-blue-100/50 shadow-blue-200 shadow-xl"
                        : "border-gray-300 bg-white/40 backdrop-blur-sm hover:bg-white/60"
                  }`}
                >
                  <div className="text-lg text-gray-700 mb-4 font-medium leading-relaxed">
                    <Markdown>{zone.definition}</Markdown>
                  </div>

                  {zone.droppedItem ? (
                    <div className="flex items-center justify-between">
                      <div
                        className={`inline-block px-6 py-3 rounded-2xl font-bold text-lg transition-all duration-300 ${
                          showResults
                            ? zone.droppedItem.word === zone.correctAnswer
                              ? "bg-green-500 text-white animate-pulse"
                              : "bg-red-500 text-white"
                            : "bg-gradient-to-r from-blue-500 to-blue-500 text-white"
                        }`}
                      >
                        {zone.droppedItem.word}
                      </div>
                      {!showResults && (
                        <button
                          onClick={() => returnToWords(zone.droppedItem!)}
                          className="ml-2 p-2 rounded-full bg-gray-200 hover:bg-gray-300 transition-colors"
                          title="Return to words"
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-16 text-gray-400 text-lg font-medium border-2 border-dashed border-gray-300 rounded-xl">
                      {tC(contentLanguage, "vocabularies.dropWordHere")}
                    </div>
                  )}

                  {showResults &&
                    zone.droppedItem &&
                    zone.droppedItem.word !== zone.correctAnswer && (
                      <div className="mt-4 p-3 bg-green-100 rounded-xl animate-slide-down">
                        <div className="text-lg font-bold text-green-700">
                          ✓ {tC(contentLanguage, "common.correctAnswer")}:{" "}
                          {zone.correctAnswer}
                        </div>
                      </div>
                    )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float 6s ease-in-out infinite;
          animation-delay: 2s;
        }

        .animate-float-slow {
          animation: float 8s ease-in-out infinite;
          animation-delay: 4s;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.5s ease-out;
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DragDropVocab;
